import { useEffect, useMemo } from 'react'
import type { CustomerSupportRequest } from '../../customerSupportApi'
import type { SelectOption } from '../../../components/SelectCustom'
import type { ShopLanguage, ShopMessageKey } from '../../../i18n'
import { buildCustomerAreaNavItems } from '../customer-area'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type UseCustomerSupportPageControllerParams = {
  supportRequests: CustomerSupportRequest[]
  supportOrderId: string
  supportCategory: string
  supportSubject: string
  language: ShopLanguage
  t: TranslateFn
  toLocalizedPath: (path: string) => string
  onSupportSubjectChange: (value: string) => void
  onSupportOrderIdChange: (value: string) => void
  onSupportCategoryChange: (value: string) => void
}

export const useCustomerSupportPageController = ({
  supportRequests,
  supportOrderId,
  supportCategory,
  supportSubject,
  language,
  t,
  toLocalizedPath,
  onSupportSubjectChange,
  onSupportOrderIdChange,
  onSupportCategoryChange,
}: UseCustomerSupportPageControllerParams) => {
  const sortedRequests = useMemo(
    () =>
      [...supportRequests].sort(
        (left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
      ),
    [supportRequests],
  )

  const openCount = useMemo(
    () => sortedRequests.filter((request) => request.status === 'open').length,
    [sortedRequests],
  )
  const inProgressCount = useMemo(
    () => sortedRequests.filter((request) => request.status === 'in_progress').length,
    [sortedRequests],
  )
  const resolvedCount = useMemo(
    () => sortedRequests.filter((request) => request.status === 'resolved' || request.status === 'closed').length,
    [sortedRequests],
  )

  const navItems = useMemo(
    () =>
      buildCustomerAreaNavItems({
        t,
        toLocalizedPath,
        language,
        supportBadge: openCount > 0 ? String(openCount) : undefined,
      }),
    [language, openCount, t, toLocalizedPath],
  )

  const supportContext = useMemo(() => {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    const orderId = params.get('orderId')?.trim() ?? ''
    if (!orderId) return null

    const orderNumber = params.get('orderNumber')?.trim() ?? ''
    const orderStatus = params.get('orderStatus')?.trim().toLowerCase() ?? ''
    const productHint = params.get('productHint')?.trim() ?? ''
    const statusLabel =
      orderStatus === 'created'
        ? language === 'it'
          ? 'In lavorazione'
          : 'Processing'
        : orderStatus === 'paid'
          ? language === 'it'
            ? 'Pagato'
            : 'Paid'
          : orderStatus === 'shipped'
            ? language === 'it'
              ? 'Spedito'
              : 'Shipped'
            : orderStatus === 'cancelled'
              ? language === 'it'
                ? 'Annullato'
                : 'Cancelled'
              : ''

    return {
      orderId,
      orderNumber,
      statusLabel,
      productHint,
    }
  }, [language])

  useEffect(() => {
    if (!supportContext) return

    const orderRef = supportContext.orderNumber || supportContext.orderId
    const initialSubject = language === 'it' ? `Supporto ordine #${orderRef}` : `Support for order #${orderRef}`

    if (supportOrderId.trim() !== supportContext.orderId) onSupportOrderIdChange(supportContext.orderId)
    if (supportCategory === 'general') onSupportCategoryChange('order')
    if (!supportSubject.trim()) onSupportSubjectChange(initialSubject)
  }, [
    language,
    onSupportCategoryChange,
    onSupportOrderIdChange,
    onSupportSubjectChange,
    supportCategory,
    supportContext,
    supportOrderId,
    supportSubject,
  ])

  const supportCategoryOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'general', label: t('customer.support.category.general') },
      { value: 'order', label: t('customer.support.category.order') },
      { value: 'shipping', label: t('customer.support.category.shipping') },
      { value: 'payment', label: t('customer.support.category.payment') },
      { value: 'privacy', label: t('customer.support.category.privacy') },
    ],
    [t],
  )

  const supportPriorityOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'normal', label: t('customer.support.priority.normal') },
      { value: 'high', label: t('customer.support.priority.high') },
      { value: 'urgent', label: t('customer.support.priority.urgent') },
    ],
    [t],
  )

  const supportOrderRef = supportContext ? supportContext.orderNumber || supportContext.orderId : ''

  return {
    sortedRequests,
    openCount,
    inProgressCount,
    resolvedCount,
    navItems,
    supportContext,
    supportCategoryOptions,
    supportPriorityOptions,
    supportOrderRef,
  }
}
